import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { HeaderProvider, useHeader } from "../../contexts/HeaderContext";
import AppHeader from "../AppHeader/AppHeader";
import Footer from "../Footer/Footer";
import EmailOTPModal from "../EmailOTPModal/EmailOTPModal";
import { useCompanyCode, appendCompanyCode } from "../../hooks/useCompanyCode";
import "./Layout.css";
import "../VenueCouponsSection/VenueCouponsSection.css"; // modal styles

/**
 * Layout wrapper component that provides Header and Footer with auth integration
 * Manages login modal and integrates with AuthContext
 */
function LayoutContent({ children, showHeader = true, showFooter = true }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { viewMode, email, wishlistCount, customerToken, showLoading, handleLogout, handleLoginSuccess, setOnLoginTrigger } = useAuth();
  const { headerData } = useHeader();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  // Track company code from URL across all pages
  useCompanyCode();

  const handleLogin = () => setShowLoginModal(true);

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  const confirmLogout = () => {
    setShowLogoutModal(false);
    handleLogout(false);
    // Stay on the page if it's a ticket purchase or event detail page —
    // those pages show member discount teasers and guest checkout for public users.
    const onTicketOrEventPage =
      location.pathname.includes("/tickets") ||
      /\/events\/[^/]+/.test(location.pathname);
    if (!onTicketOrEventPage) {
      navigate(appendCompanyCode("/perks-directory"));
    }
  };

  const cancelLogout = () => {
    setShowLogoutModal(false);
  };

  // Register login trigger with AuthContext so other components can trigger login
  useEffect(() => {
    setOnLoginTrigger(() => handleLogin);
  }, [setOnLoginTrigger]);

  // Login modal only opens from explicit user actions (login button, wishlist,
  // payment, ticket purchase, etc.) — never auto-prompted on page visit.

  const handleWishlistClick = () => {
    // If public mode, trigger login modal
    if (viewMode === "public" || !email) {
      setShowLoginModal(true);
      return;
    }

    if (customerToken) {
      navigate(`/wishlist/${customerToken}`);
    } else {
      alert("Please wait while we load your account information");
    }
  };

  const handleProfileClick = () => {
    if (customerToken && customerToken !== "public") {
      navigate(`/profile`);
    } else {
      alert("Please log in to access your profile");
    }
  };

  const onLoginSuccess = (userEmail, tokenFromModal) => {
    handleLoginSuccess(userEmail, tokenFromModal);
    setShowLoginModal(false);

    // If user is on a public coupon page, update URL to authenticated version
    if (location.pathname.includes('/coupons/public')) {
      // Replace 'public' token with actual customer token in URL
      if (tokenFromModal) {
        const newPath = location.pathname.replace('/coupons/public', `/coupons/${tokenFromModal}`);
        navigate(newPath + location.search, { replace: true });
      }
    }
    // Otherwise stay on current page and let components react to auth state change
  };

  // Check if we're on questionnaire page for new signup (not edit mode)
  const isQuestionnaireNewSignup = (location.pathname.includes('/perks-questionnaire') || location.pathname.includes('/loyalty-questionnaire')) &&
                                    !location.search.includes('edit=true');

  // Show search on all pages except questionnaire pages
  const showSearch = !isQuestionnaireNewSignup;

  // Show utility bar (Become a Partner, Language Switcher) only on loyalty directory
  const showUtilityBar = location.pathname === '/perks-directory' || location.pathname === '/';

  return (
    <>
      {/* Render header outside app-layout to ensure fixed positioning works */}
      {showHeader && !showLoading && !isQuestionnaireNewSignup && (
        <AppHeader
          viewMode={viewMode}
          wishlistCount={wishlistCount}
          onLogin={handleLogin}
          onLogout={handleLogoutClick}
          onWishlistClick={handleWishlistClick}
          onProfileClick={handleProfileClick}
          showSearch={showSearch}
          showUtilityBar={showUtilityBar}
          userLocation={headerData.userLocation}
          categories={headerData.categories}
          selectedCategory={headerData.selectedCategory}
          onCategoryChange={headerData.onCategoryChange}
          categoryIconMap={headerData.categoryIconMap}
        />
      )}

      <div className="app-layout">
        <EmailOTPModal
          isOpen={showLoginModal}
          onClose={() => setShowLoginModal(false)}
          onLoginSuccess={onLoginSuccess}
        />

      {/* Logout Confirmation Modal - Portal ensures proper z-index and mobile responsiveness */}
      {showLogoutModal && ReactDOM.createPortal(
        <div className="modal-backdrop logout-confirmation-modal" onClick={cancelLogout}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">
              Confirm Logout
            </h3>

            <p className="modal-confirm-text">
              Are you sure you want to log out?
            </p>

            <div className="modal-button-group">
              <button
                onClick={cancelLogout}
                className="modal-btn-cancel"
              >
                Cancel
              </button>
              <button
                onClick={confirmLogout}
                className="modal-btn-confirm"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

        {children}

        {showFooter && !showLoading && !isQuestionnaireNewSignup && <Footer />}
      </div>
    </>
  );
}

export default function Layout(props) {
  return (
    <HeaderProvider>
      <LayoutContent {...props} />
    </HeaderProvider>
  );
}
